import { useCallback, useEffect, useRef, useState } from 'react';
import { approve, isApproved } from '../runApproval';
import { runKey, runTask } from '../runStore';
import { copyEnvExample } from './doctorApi';
import { commandText, fixConfig, safeSteps } from './doctorModel';
import { patchDoctor, repoDoctor, runCheck, useDoctorStore } from './doctorStore';
import { useUiModalStore } from '../../../../store/uiModalStore';

/** Where a setup item's command output goes: its own log, shown under the item. */
export const setupKey = (repoPath, item) => runKey(repoPath, `setup:${item.id}`);

/**
 * Check setup for one repo block: the report (checked when the
 * configurations load, and again on return from the Terminal or when the
 * window regains focus), and running fixes.
 *
 * Nothing runs by itself. A fix runs on a click; a command not approved yet
 * — and every "Fix all safe items" — first shows the exact commands
 * (`confirm`), approved with the same SHA-256 first-run approval as
 * launch.json commands. Commands run one after the other through `runTask`
 * (terminal launch path, process tree, output in a log); the first failure
 * stops the sequence.
 */
export function useRepoDoctor(repo, configs) {
  const doctor = useDoctorStore((s) => repoDoctor(s, repo.path));
  const [confirm, setConfirm] = useState(null); // { items, opts }
  const [running, setRunning] = useState(null); // item id while its fix runs
  const [fixError, setFixError] = useState(null);
  const [envOpened, setEnvOpened] = useState(null); // repo-relative .env created → open it
  const configsRef = useRef(configs);
  configsRef.current = configs;
  const autoOpened = useRef(false);

  const recheck = useCallback(
    async (opts = {}) => {
      const list = configsRef.current || [];
      if (!list.length) return null;
      const report = await runCheck(repo.path, list, opts);
      // A fresh clone: show the checklist once by itself.
      if (report && !autoOpened.current && report.items.some((i) => i.status === 'error')) {
        autoOpened.current = true;
        patchDoctor(repo.path, { open: true });
      }
      return report;
    },
    [repo.path]
  );

  useEffect(() => {
    if (configs && configs.length) recheck();
  }, [configs, recheck]);

  // Back on the project page after "Open in terminal" (the store marks the
  // repo), or the window regains focus: re-check so what was fixed by hand turns ✓.
  const activePanel = useUiModalStore((s) => s.activeTab);
  const prevPanel = useRef(activePanel);
  useEffect(() => {
    const was = prevPanel.current;
    prevPanel.current = activePanel;
    if (was !== activePanel && activePanel === 'projects.panel' && repoDoctor(useDoctorStore.getState(), repo.path).terminal) recheck();
  }, [activePanel, recheck, repo.path]);
  useEffect(() => {
    const onFocus = () => {
      if (repoDoctor(useDoctorStore.getState(), repo.path).report) recheck();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [recheck, repo.path]);

  /** Run the fixes of `items` in order. → true when every step succeeded. */
  const runSteps = useCallback(
    async (items, { keyFor } = {}) => {
      setFixError(null);
      let created = null;
      for (const item of items) {
        const fix = item.fix;
        setRunning(item.id);
        if (fix.action === 'env_copy') {
          try {
            created = await copyEnvExample(repo.path, fix.from, fix.file);
          } catch (e) {
            if (String(e?.message || e) !== 'env_exists') {
              setFixError({ id: item.id, error: String(e?.message || e) });
              setRunning(null);
              return false;
            }
          }
          continue;
        }
        for (const cmd of fix.commands || []) {
          const key = keyFor ? keyFor(item) : setupKey(repo.path, item);
          // eslint-disable-next-line no-await-in-loop -- one after the other, by design
          const res = await runTask(repo.path, key, { ...cmd, cwd: fix.cwd }, commandText(cmd));
          if (!res.ok) {
            setFixError({ id: item.id, exitCode: res.exitCode, error: res.error });
            setRunning(null);
            await recheck({ force: true });
            return false;
          }
        }
        // this step is done: its item turns ✓ while the next one runs
        // eslint-disable-next-line no-await-in-loop -- one after the other, by design
        if (items.length > 1) await recheck();
      }
      setRunning(null);
      await recheck({ force: true });
      if (created) setEnvOpened(created);
      return true;
    },
    [repo.path, recheck]
  );

  const go = useCallback(
    async (items, opts = {}) => {
      const ok = await runSteps(items, opts);
      opts.after?.(ok);
      return ok;
    },
    [runSteps]
  );

  /**
   * Fix `items` (on a click). `always`: show the commands first even when
   * approved (Fix all). `after(ok)` runs when the sequence ended.
   */
  const requestFix = useCallback(
    async (items, opts = {}) => {
      const list = items.filter((i) => i.fix);
      if (!list.length) return;
      const pseudo = list.flatMap((it) => (it.fix.commands || []).map((c, i) => fixConfig(it, c, i)));
      const approved = await Promise.all(pseudo.map((c) => isApproved(repo.path, c)));
      if (!opts.always && approved.every(Boolean)) go(list, opts);
      else setConfirm({ items: list, opts });
    },
    [go, repo.path]
  );

  const confirmFix = useCallback(async () => {
    const c = confirm;
    setConfirm(null);
    if (!c) return;
    await Promise.all(c.items.flatMap((it) => (it.fix.commands || []).map((cmd, i) => approve(repo.path, fixConfig(it, cmd, i)))));
    go(c.items, c.opts);
  }, [confirm, go, repo.path]);

  /** "Fix all safe items" / "Install all": submodules → deps → .env, then offer Run all. */
  const fixAll = useCallback(() => {
    const steps = safeSteps(doctor.report);
    if (!steps.length) return;
    patchDoctor(repo.path, { offerRun: false, open: true });
    requestFix(steps, { always: true, after: (ok) => ok && patchDoctor(repo.path, { offerRun: true }) });
  }, [doctor.report, repo.path, requestFix]);

  return {
    doctor,
    recheck,
    requestFix,
    fixAll,
    confirm,
    confirmFix,
    cancelFix: () => setConfirm(null),
    running,
    fixError,
    envOpened,
    clearEnvOpened: () => setEnvOpened(null),
  };
}
